import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  Clock,
  ChevronDown,
  Share2,
  Copy,
  UserPlus,
} from "lucide-react";

import { Combobox } from "../components/forms/Combobox";
import { SegmentedControl } from "../components/forms/SegmentedControl";
import {
  DateOfBirthPicker,
  calcAge,
} from "../components/forms/DateOfBirthPicker";
import { MonthYearPicker } from "../components/forms/MonthYearPicker";
import { validatePostalCode } from "../components/forms/postalValidation";
import { SignUpShell } from "../components/forms/SignUpShell";
import { WhyWeAsk } from "../components/forms/WhyWeAsk";
import { ValidationSummary } from "../components/forms/ValidationSummary";
import FollowStrip from "../components/FollowStrip";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "meditalk:caregiver-register:v1";

const STEPS = [
  { id: 1, label: "About you" },
  { id: 2, label: "Location & contact" },
  { id: 3, label: "Who you care for" },
  { id: 4, label: "Consents" },
];

const DEFAULT_CONDITION = {
  condition_label: "",
  specify: "",
  diagnosis_date: "",
  severity: "",
  treatment: "",
  other_info: "",
};

const DEFAULT_RECIPIENT = {
  relationship: "",
  date_of_birth: "",
  gender: "",
  ethnicity: "",
  ethnicity_specify: "",
  conditions: [{ ...DEFAULT_CONDITION }],
};

const DEFAULT_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone_prefix: "",
  phone_number: "",
  date_of_birth: "",
  country: "",
  postal_code: "",
  care_recipients: [{ ...DEFAULT_RECIPIENT }],
  consents: {
    truthful: false,
    payment_sharing: false,
    invitation: false,
  },
};

const CONSENT_ITEMS = [
  {
    key: "truthful",
    icon: "✓",
    title: "Information is truthful",
    summary:
      "Everything I've shared above is true to the best of my knowledge.",
    full:
      "I agree that I voluntarily provided the above information and that it is true to the best of my knowledge and belief.",
  },
  {
    key: "payment_sharing",
    icon: "💳",
    title: "Payment processing",
    summary:
      "MediTalk can share my details with payment partners (e.g. e-voucher providers, MediTalk's bank Lloyds) so I can be paid for my time.",
    full:
      "To receive payment for my time and contribution, I consent to having my personal information (e.g. name, email and/or bank account information) that I provide shared with third parties (e.g. companies that provide e-voucher services or MediTalk's bank, Lloyds).",
  },
  {
    key: "invitation",
    icon: "📧",
    title: "Study invitations",
    summary:
      "MediTalk can keep my contact details on file to invite me to relevant studies.",
    full:
      "I consent to having my contact information retained by MediTalk for the purpose of being invited to take part in research studies.",
  },
];

// ============================================================
// Page
// ============================================================
export default function CaregiverSignUpPage() {
  usePageMeta({
    path: "/caregiver/sign-up",
    title: "Join MediTalk as a caregiver , Free 3-minute sign-up",
    description:
      "Create your caregiver profile in 3 minutes. Share who you care for and the conditions they live with so we can match you to studies that fit your reality.",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [options, setOptions] = useState({
    countries: [],
    conditions: [],
    popular_conditions: [],
    ethnicities: [],
    genders: [],
    severities: [],
  });
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Save & resume
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.form) {
        setForm({ ...DEFAULT_FORM, ...parsed.form });
        toast.message("Welcome back, we restored your progress.", {
          duration: 4000,
        });
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ form }));
    } catch {
      /* ignore */
    }
  }, [form]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/caregivers/form-options`)
      .then((r) => {
        if (!cancelled) setOptions(r.data);
      })
      .catch(() => toast.error("Could not load registration form options."))
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  // ------ Recipient operations ------
  const setRecipientField = (rIdx, key, value) =>
    setForm((f) => {
      const care_recipients = f.care_recipients.map((r, i) =>
        i === rIdx ? { ...r, [key]: value } : r,
      );
      return { ...f, care_recipients };
    });

  const setRecipientCondition = (rIdx, cIdx, key, value) =>
    setForm((f) => {
      const care_recipients = f.care_recipients.map((r, i) => {
        if (i !== rIdx) return r;
        const conditions = r.conditions.map((c, j) =>
          j === cIdx ? { ...c, [key]: value } : c,
        );
        return { ...r, conditions };
      });
      return { ...f, care_recipients };
    });

  const addRecipient = () =>
    setForm((f) => ({
      ...f,
      care_recipients: [...f.care_recipients, { ...DEFAULT_RECIPIENT }],
    }));

  const removeRecipient = (idx) =>
    setForm((f) => ({
      ...f,
      care_recipients:
        f.care_recipients.length === 1
          ? f.care_recipients
          : f.care_recipients.filter((_, i) => i !== idx),
    }));

  const addRecipientCondition = (rIdx) =>
    setForm((f) => {
      const care_recipients = f.care_recipients.map((r, i) =>
        i === rIdx
          ? { ...r, conditions: [...r.conditions, { ...DEFAULT_CONDITION }] }
          : r,
      );
      return { ...f, care_recipients };
    });

  const removeRecipientCondition = (rIdx, cIdx) =>
    setForm((f) => {
      const care_recipients = f.care_recipients.map((r, i) => {
        if (i !== rIdx) return r;
        if (r.conditions.length === 1) return r;
        return { ...r, conditions: r.conditions.filter((_, j) => j !== cIdx) };
      });
      return { ...f, care_recipients };
    });

  const setConsent = (key, value) =>
    setForm((f) => ({ ...f, consents: { ...f.consents, [key]: value } }));

  const tickAllConsents = () =>
    setForm((f) => ({
      ...f,
      consents: Object.fromEntries(
        Object.keys(f.consents).map((k) => [k, true]),
      ),
    }));

  const onCountryChange = (countryName) => {
    const c = options.countries.find((x) => x.name === countryName);
    setForm((f) => ({
      ...f,
      country: countryName,
      phone_prefix: c ? c.prefix : f.phone_prefix,
    }));
  };

  // ------ Per-section validators ----------------------------------------
  const validateAbout = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.date_of_birth || form.date_of_birth.length !== 10)
      e.date_of_birth = "Pick day, month and year";
    const age = calcAge(form.date_of_birth);
    if (age !== null && age < 16) e.date_of_birth = "Must be 16 or older";
    return e;
  };
  const validateLocation = () => {
    const e = {};
    if (!form.country) e.country = "Required";
    if (!form.postal_code.trim()) e.postal_code = "Required";
    else {
      const r = validatePostalCode(form.country, form.postal_code);
      if (!r.valid) e.postal_code = r.hint;
    }
    if (!form.phone_prefix.trim()) e.phone_prefix = "Required";
    if (
      !form.phone_number.trim() ||
      form.phone_number.replace(/\D/g, "").length < 6
    )
      e.phone_number = "Enter a valid mobile number";
    return e;
  };
  const validateRecipients = () => {
    const e = {};
    form.care_recipients.forEach((r, ri) => {
      r.conditions.forEach((c, ci) => {
        if (!c.condition_label)
          e[`recipient_${ri}_condition_${ci}_label`] = "Choose a condition";
        if (c.condition_label === "Other" && !c.specify?.trim())
          e[`recipient_${ri}_condition_${ci}_specify`] = "Please specify";
      });
    });
    return e;
  };
  const validateConsents = () => {
    const e = {};
    CONSENT_ITEMS.forEach((c) => {
      if (!form.consents[c.key]) e[`consent_${c.key}`] = "Required";
    });
    return e;
  };

  const completedIds = useMemo(() => {
    const set = new Set();
    if (Object.keys(validateAbout()).length === 0) set.add("about");
    if (Object.keys(validateLocation()).length === 0) set.add("location");
    if (Object.keys(validateRecipients()).length === 0) set.add("recipients");
    if (Object.keys(validateConsents()).length === 0) set.add("consents");
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const validateAll = () => {
    const e = {
      ...validateAbout(),
      ...validateLocation(),
      ...validateRecipients(),
      ...validateConsents(),
    };
    setErrors(e);
    return e;
  };

  const submit = async () => {
    setSubmitAttempted(true);
    const e = validateAll();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const order = [
        { id: "about", keys: ["first_name", "last_name", "email", "date_of_birth"] },
        { id: "location", keys: ["country", "postal_code", "phone_prefix", "phone_number"] },
        { id: "recipients", keys: Object.keys(e).filter((k) => k.startsWith("recipient_")) },
        { id: "consents", keys: Object.keys(e).filter((k) => k.startsWith("consent_")) },
      ];
      const firstSection = order.find((s) => s.keys.some((k) => k in e));
      if (firstSection) {
        const el = document.getElementById(firstSection.id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 132;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    setDuplicateEmail(null);
    try {
      const payload = {
        ...form,
        care_recipients: form.care_recipients.map((r) => ({
          ...r,
          ethnicity_specify: r.ethnicity_specify || null,
        })),
      };
      await axios.post(`${API}/caregivers/register`, payload);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        setDuplicateEmail(form.email);
        toast.error("This email is already registered.");
        const el = document.getElementById("consents");
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 132;
          window.scrollTo({ top, behavior: "smooth" });
        }
      } else if (status === 422) {
        toast.error(
          "Some fields look invalid. Please scroll up and check each section.",
        );
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : "Registration failed. Please try again or contact us.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SuccessState />;

  const consentsAllOk = Object.values(form.consents).every(Boolean);

  const SECTIONS = [
    { id: "about", label: "About you" },
    { id: "location", label: "Location & contact" },
    { id: "recipients", label: "Who you care for" },
    { id: "consents", label: "Your rights" },
  ];

  return (
    <SignUpShell
      audience="caregiver"
      mode="single-page"
      eyebrow="Caregiver sign-up"
      heroTitle="Join the MediTalk caregiver community"
      heroSubtitle="Tell us a little about you and the people you care for. We'll only get in touch about studies that match. You can withdraw at any time."
      timeEstimate="Takes about 4 minutes"
      backTo="/caregiver"
      backLabel="Back to Caregiver Hub"
      sections={SECTIONS}
      completedIds={completedIds}
      mobileCta={
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="w-full btn-teal !py-2.5 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          data-testid="submit-register-btn-mobile"
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              Submit registration
              <Check size={18} />
            </>
          )}
        </button>
      }
    >
      <div data-testid="caregiver-signup-form" className="space-y-14">
        <section id="about" className="scroll-mt-32">
          <Step1 form={form} setField={setField} errors={errors} />
        </section>

        <section id="location" className="scroll-mt-32 pt-2 border-t border-[var(--light-border)]">
          <Step2
            form={form}
            setField={setField}
            onCountryChange={onCountryChange}
            options={options}
            optionsLoading={optionsLoading}
            errors={errors}
          />
        </section>

        <section id="recipients" className="scroll-mt-32 pt-2 border-t border-[var(--light-border)]">
          <Step3
            form={form}
            setRecipientField={setRecipientField}
            setRecipientCondition={setRecipientCondition}
            addRecipient={addRecipient}
            removeRecipient={removeRecipient}
            addRecipientCondition={addRecipientCondition}
            removeRecipientCondition={removeRecipientCondition}
            options={options}
            optionsLoading={optionsLoading}
            errors={errors}
          />
        </section>

        <section id="consents" className="scroll-mt-32 pt-2 border-t border-[var(--light-border)]">
          <Step4
            form={form}
            setConsent={setConsent}
            tickAll={tickAllConsents}
            errors={errors}
            duplicateEmail={duplicateEmail}
          />
        </section>

        <div className="hidden md:block pt-6 border-t border-[var(--light-border)]">
          <ValidationSummary
            visible={submitAttempted}
            sections={SECTIONS.map((s) => ({ ...s, ok: completedIds.has(s.id) }))}
          />
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="w-full md:w-auto btn-teal inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="submit-register-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                Submit registration
                <Check size={18} />
              </>
            )}
          </button>
          {!consentsAllOk && (
            <p className="mt-3 text-sm text-[var(--text-muted)]">
              You'll need to confirm all 3 statements above before submitting.
            </p>
          )}
        </div>
      </div>
    </SignUpShell>
  );
}

// ============================================================
// Sub-components (mostly mirrored from PatientSignUpPage but with caregiver semantics)
// ============================================================

function FieldLabel({ children, error, helper }) {
  return (
    <div className="mb-2">
      <label className="block text-[15px] font-semibold text-[var(--charcoal)]">
        {children}
        {error && (
          <span className="ml-2 text-xs font-medium text-red-600">{error}</span>
        )}
      </label>
      {helper && !error && (
        <span className="text-[12.5px] text-[#475569]">{helper}</span>
      )}
    </div>
  );
}

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] placeholder:text-[var(--text-muted)]/70 transition-all focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20";
const inputClsErr =
  "w-full h-12 px-4 rounded-xl border border-red-300 bg-white text-[15px] text-[var(--charcoal)] transition-all focus:outline-none focus:border-red-400 focus:ring-4 focus:ring-red-200/40";
const selectCls = inputCls + " appearance-none cursor-pointer pr-10 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23475569%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-no-repeat bg-[right_0.875rem_center]";
const textareaCls =
  "w-full px-4 py-3 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] placeholder:text-[var(--text-muted)]/70 transition-all focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20";

function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-[var(--bone-soft)] rounded-lg ${className}`} />;
}

// ----------- Step 1 -----------
function Step1({ form, setField, errors }) {
  const age = calcAge(form.date_of_birth);
  return (
    <div className="space-y-5" data-testid="register-step-1">
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
          About you
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Just the caregiver's basics. We'll ask about the people you care for in a moment.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <FieldLabel error={errors.first_name}>First name *</FieldLabel>
          <input data-testid="reg-first-name" className={errors.first_name ? inputClsErr : inputCls}
            value={form.first_name} onChange={(e) => setField("first_name", e.target.value)}
            autoComplete="given-name" />
        </div>
        <div>
          <FieldLabel error={errors.last_name}>Last name *</FieldLabel>
          <input data-testid="reg-last-name" className={errors.last_name ? inputClsErr : inputCls}
            value={form.last_name} onChange={(e) => setField("last_name", e.target.value)}
            autoComplete="family-name" />
        </div>
        <div className="md:col-span-2">
          <FieldLabel error={errors.email}>Email *</FieldLabel>
          <input data-testid="reg-email" type="email" inputMode="email"
            className={errors.email ? inputClsErr : inputCls}
            value={form.email} onChange={(e) => setField("email", e.target.value)}
            autoComplete="email" />
        </div>
        <div className="md:col-span-2">
          <FieldLabel error={errors.date_of_birth} helper="Used to confirm age eligibility for each study">
            Date of birth *
          </FieldLabel>
          <DateOfBirthPicker data-testid="reg-dob"
            value={form.date_of_birth}
            onChange={(v) => setField("date_of_birth", v)} />
          {age !== null && (
            <p className="mt-1 text-xs text-[var(--teal-dark)]">You're {age} years old.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ----------- Step 2 -----------
function Step2({ form, setField, onCountryChange, options, optionsLoading, errors }) {
  const countryOptions = useMemo(
    () => options.countries.map((c) => ({ value: c.name, label: c.name })),
    [options.countries],
  );
  return (
    <div className="space-y-5" data-testid="register-step-2">
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">Location & contact</h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Many studies are country-specific. We'll use this to filter the opportunities we send.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <FieldLabel error={errors.country}>Country *</FieldLabel>
          {optionsLoading ? <Skeleton className="h-11" /> : (
            <Combobox data-testid="reg-country" options={countryOptions}
              value={form.country} onChange={onCountryChange}
              placeholder="Type to search…" error={!!errors.country} />
          )}
        </div>
        <div className="md:col-span-2">
          <FieldLabel error={errors.postal_code}>Postal / ZIP code *</FieldLabel>
          <input data-testid="reg-postal" className={errors.postal_code ? inputClsErr : inputCls}
            value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)}
            autoComplete="postal-code" />
        </div>
        <div>
          <FieldLabel error={errors.phone_prefix}>Country code *</FieldLabel>
          <input data-testid="reg-phone-prefix" className={errors.phone_prefix ? inputClsErr : inputCls}
            value={form.phone_prefix} onChange={(e) => setField("phone_prefix", e.target.value)}
            placeholder="+1" inputMode="tel" />
        </div>
        <div>
          <FieldLabel error={errors.phone_number}>Mobile number *</FieldLabel>
          <input data-testid="reg-phone-number" className={errors.phone_number ? inputClsErr : inputCls}
            value={form.phone_number} onChange={(e) => setField("phone_number", e.target.value)}
            placeholder="6187805565" autoComplete="tel-national" inputMode="tel" />
        </div>
      </div>
      <WhyWeAsk>
        We only call if a study is a strong match. No marketing calls, ever.
      </WhyWeAsk>
    </div>
  );
}

// ----------- Step 3: People you care for -----------
function Step3({
  form, setRecipientField, setRecipientCondition,
  addRecipient, removeRecipient, addRecipientCondition, removeRecipientCondition,
  options, optionsLoading, errors,
}) {
  const conditionChoices = useMemo(() => {
    const list = options.conditions || [];
    return list.includes("Other") ? list : [...list, "Other"];
  }, [options.conditions]);

  return (
    <div className="space-y-6" data-testid="register-step-3">
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
          People you care for
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Add a card for each person you provide care for. Within each card, list every condition they live with. Choose <strong>Other</strong> if it's not in the list.
        </p>
      </div>

      {form.care_recipients.map((recipient, rIdx) => (
        <RecipientCard
          key={rIdx}
          rIdx={rIdx}
          recipient={recipient}
          setRecipientField={setRecipientField}
          setRecipientCondition={setRecipientCondition}
          addRecipientCondition={addRecipientCondition}
          removeRecipientCondition={removeRecipientCondition}
          removeRecipient={removeRecipient}
          conditionChoices={conditionChoices}
          options={options}
          optionsLoading={optionsLoading}
          errors={errors}
          canRemove={form.care_recipients.length > 1}
        />
      ))}

      <button
        type="button"
        onClick={addRecipient}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-2xl bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-[15px] hover:bg-[var(--lime-dark)] transition-all shadow-[0_8px_24px_-12px_rgba(196,247,75,0.7)] hover:shadow-[0_12px_30px_-10px_rgba(196,247,75,0.9)] hover:-translate-y-0.5"
        data-testid="add-recipient-btn"
      >
        <UserPlus size={18} strokeWidth={2.5} />
        Add another person you care for
      </button>
    </div>
  );
}

function RecipientCard({
  rIdx, recipient,
  setRecipientField, setRecipientCondition,
  addRecipientCondition, removeRecipientCondition, removeRecipient,
  conditionChoices, options, optionsLoading, errors, canRemove,
}) {
  return (
    <div
      className="rounded-2xl border border-[var(--light-border)] p-6 space-y-5 bg-[var(--bone-soft)] transition-all hover:border-[var(--lime)] hover:shadow-[0_10px_30px_-12px_rgba(196,247,75,0.35)] animate-[stepIn_300ms_ease-out_forwards]"
      data-testid={`recipient-${rIdx}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-[var(--charcoal)] inline-flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-[var(--lime)] inline-flex items-center justify-center text-[var(--charcoal)] text-xs font-bold">
            {rIdx + 1}
          </span>
          Person you care for #{rIdx + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => removeRecipient(rIdx)}
            className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
            data-testid={`remove-recipient-${rIdx}`}
          >
            <Trash2 size={16} /> Remove person
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel helper="e.g. Son, Mother, Partner">
            Relationship to you
          </FieldLabel>
          <input
            data-testid={`recipient-${rIdx}-relationship`}
            className={inputCls}
            value={recipient.relationship || ""}
            onChange={(e) => setRecipientField(rIdx, "relationship", e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div>
          <FieldLabel>Their date of birth</FieldLabel>
          <DateOfBirthPicker
            data-testid={`recipient-${rIdx}-dob`}
            value={recipient.date_of_birth || ""}
            onChange={(v) => setRecipientField(rIdx, "date_of_birth", v)}
          />
        </div>
        <div>
          <FieldLabel>Their gender</FieldLabel>
          {optionsLoading ? <Skeleton className="h-11" /> : (
            <select
              data-testid={`recipient-${rIdx}-gender`}
              className={selectCls}
              value={recipient.gender || ""}
              onChange={(e) => setRecipientField(rIdx, "gender", e.target.value)}
            >
              <option value="">Select…</option>
              {options.genders.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <FieldLabel>Their ethnicity</FieldLabel>
          {optionsLoading ? <Skeleton className="h-11" /> : (
            <select
              data-testid={`recipient-${rIdx}-ethnicity`}
              className={selectCls}
              value={recipient.ethnicity || ""}
              onChange={(e) => setRecipientField(rIdx, "ethnicity", e.target.value)}
            >
              <option value="">Select…</option>
              {options.ethnicities.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          )}
        </div>
        {recipient.ethnicity === "Multiple ethnicity/ Other (please specify)" && (
          <div className="md:col-span-2">
            <FieldLabel>Please specify ethnicity</FieldLabel>
            <input
              className={inputCls}
              value={recipient.ethnicity_specify || ""}
              onChange={(e) =>
                setRecipientField(rIdx, "ethnicity_specify", e.target.value)
              }
            />
          </div>
        )}
      </div>

      {/* Conditions for this recipient */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-[var(--charcoal)]">
          Their conditions
        </h4>
        {recipient.conditions.map((cond, cIdx) => (
          <ConditionBlock
            key={cIdx}
            rIdx={rIdx}
            cIdx={cIdx}
            cond={cond}
            setRecipientCondition={setRecipientCondition}
            removeRecipientCondition={removeRecipientCondition}
            conditionChoices={conditionChoices}
            options={options}
            optionsLoading={optionsLoading}
            errors={errors}
            canRemove={recipient.conditions.length > 1}
          />
        ))}
        <button
          type="button"
          onClick={() => addRecipientCondition(rIdx)}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] transition-all shadow-[0_6px_18px_-10px_rgba(196,247,75,0.7)] hover:-translate-y-0.5"
          data-testid={`add-condition-${rIdx}`}
        >
          <Plus size={14} strokeWidth={2.5} />
          Add another condition for this person
        </button>
      </div>
    </div>
  );
}

function ConditionBlock({
  rIdx, cIdx, cond, setRecipientCondition, removeRecipientCondition,
  conditionChoices, options, optionsLoading, errors, canRemove,
}) {
  return (
    <div className="rounded-2xl border border-[var(--light-border)] bg-white p-5 space-y-3 transition-all hover:border-[var(--lime)]/60 animate-[stepIn_300ms_ease-out_forwards]">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-[var(--charcoal)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--lime)]" />
          Condition #{cIdx + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => removeRecipientCondition(rIdx, cIdx)}
            className="text-xs text-[var(--text-muted)] hover:text-red-600 inline-flex items-center gap-1 transition-colors"
            data-testid={`remove-condition-${rIdx}-${cIdx}`}
          >
            <Trash2 size={14} /> Remove
          </button>
        )}
      </div>
      <div>
        <FieldLabel error={errors[`recipient_${rIdx}_condition_${cIdx}_label`]}>
          Condition *
        </FieldLabel>
        {optionsLoading ? <Skeleton className="h-11" /> : (
          <Combobox
            data-testid={`recipient-${rIdx}-condition-${cIdx}-label`}
            options={conditionChoices}
            value={cond.condition_label}
            onChange={(v) => setRecipientCondition(rIdx, cIdx, "condition_label", v)}
            placeholder="Type to search 200+ conditions…"
            pinnedValues={options.popular_conditions || []}
            pinnedLabel="Most common"
            error={!!errors[`recipient_${rIdx}_condition_${cIdx}_label`]}
          />
        )}
      </div>
      {cond.condition_label === "Other" && (
        <div>
          <FieldLabel error={errors[`recipient_${rIdx}_condition_${cIdx}_specify`]}>
            Please specify *
          </FieldLabel>
          <input
            className={errors[`recipient_${rIdx}_condition_${cIdx}_specify`] ? inputClsErr : inputCls}
            value={cond.specify || ""}
            onChange={(e) => setRecipientCondition(rIdx, cIdx, "specify", e.target.value)}
          />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <FieldLabel>Approx. Diagnosis Date</FieldLabel>
          <MonthYearPicker
            value={cond.diagnosis_date || ""}
            onChange={(v) => setRecipientCondition(rIdx, cIdx, "diagnosis_date", v)}
          />
        </div>
        <div>
          <FieldLabel>Severity</FieldLabel>
          <SegmentedControl
            data-testid={`recipient-${rIdx}-condition-${cIdx}-severity`}
            options={["Mild", "Moderate", "Severe"]}
            value={cond.severity || ""}
            onChange={(v) => setRecipientCondition(rIdx, cIdx, "severity", v)}
            ariaLabel="Severity"
          />
        </div>
      </div>
      <div>
        <FieldLabel>Treatment</FieldLabel>
        <input
          className={inputCls}
          value={cond.treatment || ""}
          onChange={(e) => setRecipientCondition(rIdx, cIdx, "treatment", e.target.value)}
          placeholder="e.g. Medication, therapy, none"
        />
      </div>
      <div>
        <FieldLabel>Anything else we should know? (optional)</FieldLabel>
        <textarea
          rows={2}
          className={textareaCls}
          value={cond.other_info || ""}
          onChange={(e) => setRecipientCondition(rIdx, cIdx, "other_info", e.target.value)}
        />
      </div>
    </div>
  );
}

// ----------- Step 4 -----------
function Step4({ form, setConsent, tickAll, errors, duplicateEmail }) {
  const allTicked = Object.values(form.consents).every(Boolean);
  return (
    <div className="space-y-5" data-testid="register-step-4">
      <div>
        <h2 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-[var(--charcoal)]">
          Your rights, in plain English
        </h2>
        <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed max-w-xl">
          Three short statements that protect you. Each has a plain summary
          and the full legal wording one tap away. All three are required.
        </p>
      </div>

      {duplicateEmail && (
        <div
          className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-[var(--charcoal)] flex items-start gap-3"
          data-testid="duplicate-email-banner"
        >
          <span className="mt-0.5 text-amber-600">●</span>
          <div>
            <p className="font-semibold mb-1">You're already registered</p>
            <p className="text-[var(--text-muted)] leading-relaxed">
              with <span className="font-mono">{maskEmail(duplicateEmail)}</span>.{" "}
              <Link to="/caregiver/update" className="text-[var(--teal)] underline font-medium">
                Update your details →
              </Link>
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap p-4 rounded-2xl bg-[var(--bone-soft)] border border-[var(--light-border)]">
        <p className="text-sm text-[var(--charcoal)] font-medium">
          <span className="text-[var(--teal)] font-bold">
            {Object.values(form.consents).filter(Boolean).length}
          </span>
          <span className="text-[var(--text-muted)]"> of 3 confirmed</span>
        </p>
        <button
          type="button"
          onClick={tickAll}
          disabled={allTicked}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[var(--lime)] text-[var(--dark-bg)] hover:bg-[var(--lime-dark)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="tick-all-consents-btn"
        >
          <Check size={16} strokeWidth={3} />
          {allTicked ? "All confirmed" : "Confirm all (recommended)"}
        </button>
      </div>

      <div className="space-y-3">
        {CONSENT_ITEMS.map((c) => (
          <ConsentItem
            key={c.key}
            item={c}
            checked={form.consents[c.key]}
            error={errors[`consent_${c.key}`]}
            onChange={(v) => setConsent(c.key, v)}
          />
        ))}
      </div>
    </div>
  );
}

function ConsentItem({ item, checked, error, onChange }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`rounded-2xl border transition-all overflow-hidden ${
        checked
          ? "border-[var(--lime)] bg-[var(--lime)]/[0.06]"
          : error
          ? "border-red-300 bg-red-50/40"
          : "border-[var(--light-border)] bg-white hover:border-[var(--charcoal)]/20"
      }`}
    >
      <label
        htmlFor={`consent-${item.key}`}
        className="flex gap-3 items-start p-4 cursor-pointer"
      >
        <span
          className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-md border-2 flex-shrink-0 transition-all ${
            checked
              ? "bg-[var(--lime)] border-[var(--lime)]"
              : "bg-white border-[var(--light-border)]"
          }`}
          aria-hidden="true"
        >
          {checked && (
            <Check size={14} strokeWidth={3.5} className="text-[var(--dark-bg)]" />
          )}
        </span>
        <input
          id={`consent-${item.key}`}
          data-testid={`consent-${item.key}`}
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className="min-w-0 flex-1">
          <span className="block font-semibold text-[var(--charcoal)] text-sm md:text-base mb-1">
            {item.title}
          </span>
          <p className="text-sm text-[var(--text-muted)] leading-relaxed">
            {item.summary}
          </p>
        </div>
      </label>
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="px-4 pb-3 -mt-1 inline-flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--charcoal)] transition-colors"
      >
        <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        {expanded ? "Hide full text" : "Read full text"}
      </button>
      {expanded && (
        <p className="px-4 pb-4 text-xs text-[var(--text-muted)] leading-relaxed border-t border-[var(--light-border)] pt-3">
          {item.full}
        </p>
      )}
    </div>
  );
}

// ============================================================
// Helpers / Success
// ============================================================
function maskEmail(email) {
  if (!email) return "";
  const [local, domain] = email.split("@");
  if (!domain) return email;
  return `${local.slice(0, 1)}${"*".repeat(Math.max(local.length - 1, 1))}@${domain}`;
}

function SuccessState() {
  const [copied, setCopied] = useState(false);
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/caregiver`
      : "https://meditalk.world/caregiver";

  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "MediTalk",
          text:
            "Get paid to share your caregiver experience with MediTalk. Fully remote interviews, surveys and bulletin boards.",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Link copied to clipboard");
        setTimeout(() => setCopied(false), 2200);
      }
    } catch {
      /* user dismissed */
    }
  };

  return (
    <div className="min-h-screen">
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-[-160px] left-1/2 -translate-x-1/2 h-[480px] w-[480px] rounded-full bg-[var(--lime)]/15 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-[100px] -right-32 h-72 w-72 rounded-full bg-[var(--lime)]/10 blur-3xl"
        />
        <div className="container-main py-20 md:py-24 max-w-3xl text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--lime)] mb-7 animate-[bounceIn_500ms_ease-out]">
            <Check size={40} strokeWidth={3} className="text-[var(--dark-bg)]" />
          </div>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
            <span className="w-6 h-px bg-[var(--lime)]" />
            Welcome to the community
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white mb-5 leading-[1.05]">
            Your voice
            <span className="text-[var(--lime)]"> matters.</span>
          </h1>
          <p className="text-lg text-white/70 mb-12 max-w-xl mx-auto leading-relaxed">
            Thanks for registering. We'll only get in touch when there's a
            study that matches, never before.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <Link
              to="/caregiver/studies"
              className="group rounded-2xl bg-white/[0.04] border border-white/10 p-6 text-left hover:bg-white/[0.08] hover:border-[var(--lime)]/40 transition-all"
              data-testid="success-view-studies"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-2">
                Step 1
              </p>
              <h3 className="font-['Outfit'] font-semibold text-white mb-2">
                Browse open studies
              </h3>
              <p className="text-xs text-white/60 leading-relaxed mb-4">
                See what's open right now and express interest in anything that fits.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--lime)]">
                Browse <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 text-left">
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-2">
                Step 2
              </p>
              <h3 className="font-['Outfit'] font-semibold text-white mb-2">
                Check your inbox
              </h3>
              <p className="text-xs text-white/60 leading-relaxed">
                When a study matches your profile, we'll email you. Reply to opt in.
              </p>
            </div>
            <button
              type="button"
              onClick={onShare}
              className="group rounded-2xl bg-[var(--lime)]/10 border border-[var(--lime)]/30 p-6 text-left hover:bg-[var(--lime)]/15 transition-all"
              data-testid="success-share"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-2">
                Step 3
              </p>
              <h3 className="font-['Outfit'] font-semibold text-white mb-2">
                {copied ? "Link copied" : "Share MediTalk"}
              </h3>
              <p className="text-xs text-white/60 leading-relaxed mb-4">
                Know another caregiver whose voice should be heard?
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--lime)]">
                {copied ? <Copy size={14} /> : <Share2 size={14} />}
                {copied ? "Copied" : "Share"}
              </span>
            </button>
          </div>

          {/* Soft secondary touchpoint , a follow strip for visitors
              who'd rather hear from us on social than via email. */}
          <div className="mb-10 text-left">
            <FollowStrip
              variant="dark"
              source="caregiver-success"
              campaign="post-signup"
              medium="success-page"
              title="Follow us for new study announcements"
              subtitle="We post new studies on Facebook, Instagram, TikTok and X , often before they go live on the site."
              testId="caregiver-success-follow-strip"
            />
          </div>

          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 text-sm text-white/65 hover:text-white transition-colors"
            data-testid="success-home"
          >
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>
      </section>
      <style>{`
        @keyframes bounceIn {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
